import axios from 'axios';

import config from '../../config/config';
import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';

export interface BrandingData {
  shortName: string;
  clientName: string;
  logo: string;
  favicon: string;
  theme: any;
  footer: any;
  webSocketUrl: string;
  qrCode: string;
  useSharedDatabase: boolean;
}

const STORAGE_KEY = 'app_branding_config';

/**
 * Service to handle dynamic branding operations
 */
const brandingService = {
  /**
   * Fetches branding configuration for the current domain.
   * @param {string} domain - Optional domain override.
   * @returns {any} The fetched branding data.
   */
  fetchBranding: async (domain?: string): Promise<BrandingData | null> => {
    const targetDomain = domain || getFirstSubdomain();

    if (!targetDomain) {
      return null;
    }

    // Check cache first - if exists, don't try the API call as requested
    const cachedLocal = localStorage.getItem(STORAGE_KEY);
    if (cachedLocal) {
      try {
        return JSON.parse(cachedLocal);
      } catch (error) {
        console.error('Failed to parse cached branding:', error);
      }
    }

    try {
      // Note: Using direct axios call as base/get utility might have default headers logic
      // that we want to avoid or specifically set here (like Authorization: internal)
      const response = await axios.get(
        `${config.commonApiUrl}api/saas-client-config/v2/client-config`,
        {
          headers: {
            Authorization: 'internal',
            domain: targetDomain,
          },
        },
      );

      if (response.data) {
        // Store in localStorage for persistence
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data));
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // Try to return from cache if API fails
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (error_) {
          return null;
        }
      }
      return null;
    }
  },

  /**
   * Gets branding from localStorage cache.
   * @returns {any} The cached branding data.
   */
  getCachedBranding: (): BrandingData | null => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error_) {
        return null;
      }
    }
    return null;
  },

  /**
   * Clears branding cache.
   */
  clearCache: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
};

export default brandingService;
